<template>
  <v-stage
    ref="stage"
    :config="stageConfig"
    @mousedown="handleStageMouseDown"
    @touchstart="handleStageMouseDown">
    <v-layer ref="layer">
      <v-group
        v-for="clusterPreview in clustersPreviews"
        :name="clusterNodeName"
        :key="clusterPreview.key"
        :config="clusterPreview.groupConfig"
        @dragmove="onClusterDragMove"
        @dragend="onClusterDragEnd">
        <v-rect :config="clusterPreview.rectangleConfig" @transform="handleTransform"></v-rect>
        <v-text :config="clusterPreview.textConfig"></v-text>
      </v-group>
      <v-group
        v-for="productPreview in productsPreviews"
        :name="productNodeName"
        :key="productPreview.productId"
        :config="productPreview.groupConfig"
        @dragmove="onProductDragMove"
        @dragend="onProductDragEnd">
        <v-image
          :config="productPreview.imageConfig"
          @transform="handleTransform">
        </v-image>
        <v-text :config="productPreview.textConfig"></v-text>
      </v-group>
      <v-transformer
        ref="transformer"
        :config="transformerConfig" />
    </v-layer>
  </v-stage>
</template>

<script>
import { getStaticUrl } from '@/helpers';
import Konva, { Image as KonvaImage, Rect as KonvaRect } from 'konva';
import { FAMILY_PREVIEW_EDITOR_CONFIG as CONFIG } from '../constants/familyPreviewEditor';
import CanvasClusterPositioningHelper from '@/helpers/familyPreview/canvasClusterPositioningHelper';
import CanvasProductPositioningHelper from '@/helpers/familyPreview/canvasProductPositioningHelper';
import SnapType from '@/helpers/familyPreview/snapType';
import LineOrientation from '@/helpers/familyPreview/lineOrientation';
import AssetsApiService from '@/services/assetsServices/assetsApiService';
import { exportStageSVG } from 'vue-konva-to-svg';
import FamilyPreviewService from '@/services/familyPreviewServices/familyPreviewService';
import { NAMES } from '@/constants/productNames';
import ProductService from '@/services/productService';

export default {
  props: {
    clusters: {
      type: Array,
      default: []
    },
    selectedFamily: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      productsPreviews: [],
      clustersPreviews: [],
      selectedShape: undefined,
      stage: undefined,
      layer: undefined,
      resizeObserver: null,
      clustersProducts: {},
      clustersStage: [],
      stageConfig: {
        width: 1920,
        height: 953
      },
      currentHeight: 0,
      currentWidth: 0,
      transformerConfig: {
        rotateEnabled: false
      },
      canvasData: {}
    }
  },

  computed: {
    staticUrl() {
      return getStaticUrl(null, this.$route.name);
    },
    clusterNodeName() {
      return CONFIG.global.cluster.nodeName;
    },
    productNodeName() {
      return CONFIG.global.product.nodeName;
    }
  },

  watch: {
    clusters: function (newClusters) {
      this.loadClustersPreviews(newClusters);
      this.selectedShape = undefined;
      this.updateTransformer();
    },

    selectedFamily: function() {
      this.canvasData = FamilyPreviewService.getFamilyPreview(this.selectedFamily);
      if (this.canvasData?.familyProducts.length > 0) {
        this.addProducts(this.canvasData.familyProducts);
      }
    },
  },

  created() {
    this.canvasData = FamilyPreviewService.getFamilyPreview(CONFIG.defaultFamilyName);
    if (this.canvasData?.familyProducts.length > 0) {
      this.addProducts(this.canvasData.familyProducts);
    }
  },

  mounted() {
    this.stage = this.$refs.stage.getStage();
    this.layer = this.$refs.layer.getNode();

    this.currentHeight = window.innerHeight / this.stage.height();
    this.currentWidth = window.innerWidth / this.stage.width();

    this.resizeObserver = new ResizeObserver(this.onResize);
    this.resizeObserver.observe(this.$el);
  },

  destroyed() {
    this.resizeObserver.unobserve(this.$el);
  },

  methods: {
    addProducts(products) {
      const orthoProducts = products
        .filter(product => !!product.orthoPath);

      orthoProducts.forEach(product => {
        this.toProductPreview(product).then(data => {
            const preview = [];
            preview.push(data);
            this.productsPreviews.splice(this.productsPreviews.length, 0, ...preview);
          })
      });
    },

    removeProducts(productsIds) {
      this.productsPreviews = this.productsPreviews.filter(preview => !productsIds.includes(preview.productId));
      this.selectedShape = undefined;
      this.updateTransformer();
    },

    async toProductPreview(product) {
      const image = await this.getImageObject(product.orthoPath);
      
      let imageConfig = {};
      let textConfig = {};

      if (this.canvasData?.familyProducts.length > 0 && product?.pointX) {
        imageConfig = {
          productId: product.productId,
          orthoPath: product.orthoPath,
          image: image,
          x: product.pointX * this.currentWidth,
          y: product.pointY * this.currentHeight,
          scaleX: product.scaleX === 0 ? 0.1 * this.currentWidth : product.scaleX * this.currentWidth,
          scaleY: product.scaleY === 0 ? 0.1 * this.currentHeight: product.scaleY * this.currentHeight,
        };

        textConfig = {
          productId: product.productId,
          text: this.getProductName(product.productId),
          align: CONFIG.text.align,
          fontSize: 13.5 * this.currentHeight,
          fill: CONFIG.text.fill,
          padding: 5,
          x: product.pointX * this.currentWidth,
          y: product.pointY * this.currentHeight,
          width: 110 * this.currentWidth
        };

      } else {
        imageConfig = {
          productId: product.productId,
          orthoPath: product.orthoPath,
          image: image,
          x: 0,
          y: 0,
          scaleY: 0.1 * this.currentHeight,
          scaleX: 0.1 * this.currentWidth
        };

        textConfig = {
          productId: product.productId,
          text: this.getProductName(product.productId),
          align: CONFIG.text.align,
          fontSize: CONFIG.text.fontSize * this.currentHeight,
          fill: CONFIG.text.fill,
          x: 0,
          y: 0,
          width: 110 * this.currentWidth
        };
      }
      

      const marginBottom = CONFIG.global.image.marginBottom;

      if (!!image) {
        image.onload = function() {
          imageConfig.height = image.height;
          imageConfig.width = image.width;
          textConfig.x = textConfig.x + imageConfig.width * imageConfig.scaleX / 2 - textConfig.width / 2
          textConfig.y = textConfig.y + imageConfig.height * imageConfig.scaleY + marginBottom;
        };
      }

      return {
        productId: product.productId,
        imageConfig: imageConfig,
        textConfig: textConfig,
        groupConfig: {
          draggable: true,
          id: product.productId,
        }
      };
    },

    async getImageObject(filename) {
      try {
        const image = new Image();
        const dataImage = await AssetsApiService.getImageUrl(filename);
        image.src = dataImage;

        return image;
      } catch (error) {
        console.error(error);

        return null;
      }
    },

    onResize() {
      this.updateStageSize();
    },
    updateStageSize() {
      this.stage.width(this.$el.clientWidth);
      this.stage.height(this.$el.clientHeight);
    },
    handleTransform(e) {
      const selectedShape = e.target;

      if (selectedShape instanceof KonvaImage) {
        const productId = selectedShape.attrs.productId;

        const productPreview = this.productsPreviews.find(preview => preview.productId === productId);

        if (!productPreview) {
          return;
        }

        productPreview.textConfig.y = selectedShape.y() + selectedShape.height() * selectedShape.scaleY() + CONFIG.global.image.marginBottom;
        productPreview.textConfig.x = selectedShape.x() + selectedShape.width() * selectedShape.scaleX() / 2 - (productPreview.textConfig.width / 2);

        return;
      }

      if (selectedShape instanceof KonvaRect) {
        const clusterName = selectedShape.attrs.id;
        const clusterPreview = this.clustersPreviews.find(cluster => cluster.label === clusterName);
        const textCluster = new Konva.Text(clusterPreview.textConfig);

        clusterPreview.textConfig.y = selectedShape.y() + selectedShape.height() * selectedShape.scaleY() - (textCluster.height() / 2) * selectedShape.scaleY();
        clusterPreview.textConfig.x = selectedShape.x() + selectedShape.width() * selectedShape.scaleX() / 2 - (textCluster.width() / 2) * selectedShape.scaleX();

        return;
      }
    },
    handleStageMouseDown(e) {
      const selectedShape = e.target;

      if (selectedShape === selectedShape.getStage()) {
        this.selectedShape = undefined;
        this.updateTransformer();

        return;
      }

      const isClickedOnTransformer = selectedShape.getParent().className === 'Transformer';

      if (isClickedOnTransformer) {
        return;
      }

      this.selectedShape = selectedShape instanceof KonvaImage || selectedShape instanceof KonvaRect
        ? e.target
        : undefined;

      this.updateTransformer();
    },
    updateTransformer() {
      const transformerNode = this.$refs.transformer.getNode();

      if (this.selectedShape === undefined) {
        transformerNode.nodes([]);
        return;
      }

      if (this.selectedShape === transformerNode.node()) {
        return;
      }

      transformerNode.nodes([this.selectedShape]);
    },
    loadClustersPreviews(clusters) {
      const stageWidth = this.stage.width();

      const clusterWidth = stageWidth / clusters.length;
      const clusterHeight = this.stage.height() - 2 * CONFIG.global.cluster.margin - CONFIG.global.text.fontSize / 2;

      if (this.canvasData?.familyClusters.length > 0) {
        this.clustersPreviews = this.canvasData.familyClusters.map(cluster => {
          const clusterText = new Konva.Text({
            fontSize: 24 * this.currentHeight,
            text: FamilyPreviewService.getClusterName(cluster.clusterName)
          });

          if (clusterText.width() > cluster.width * this.currentWidth) {
            clusterText.width(cluster.width * this.currentWidth);
            clusterText.align('left')
          } else {
            clusterText.align('center');
          }

          return {
            key: cluster.clusterName,
            label: cluster.clusterName,
            rectangleConfig: {
              id: cluster.clusterName,
              name: CONFIG.rectangle.nodeName,
              width: cluster.width * this.currentWidth,
              height: cluster.height * this.currentHeight,
              fill: CONFIG.rectangle.fill,
              stroke: CONFIG.rectangle.stroke,
              strokeWidth: CONFIG.rectangle.strokeWidth
            },
            textConfig: {
              text: clusterText.text(),
              fontSize: 24 * this.currentHeight,
              fill: CONFIG.text.fill,
              align: clusterText.align(),
              width: clusterText.width(),
              x: cluster.width / 2 * this.currentWidth - clusterText.width() / 2,
              y: cluster.height * this.currentHeight - clusterText.height() / 2,
            },
            groupConfig: {
              draggable: true,
              x: cluster.pointX * this.currentWidth,
              y: cluster.pointY * this.currentHeight,
              id: cluster.clusterName
            }
          }
        })
      } else {
        this.clustersPreviews = clusters.map((cluster, index) => {
          return {
            key: cluster.label,
            label: cluster.label,
            rectangleConfig: {
              id: cluster.clusterName,
              name: CONFIG.rectangle.nodeName,
              width: (clusterWidth - 2 * CONFIG.global.cluster.margin) * this.currentWidth,
              height: clusterHeight * this.currentHeight,
              fill: CONFIG.rectangle.fill,
              stroke: CONFIG.rectangle.stroke,
              strokeWidth: CONFIG.rectangle.strokeWidth
            },
            textConfig: {
              text: cluster.label,
              fontSize: CONFIG.global.text.fontSize * this.currentHeight,
              fill: CONFIG.text.fill,
              x: 0 + (clusterWidth - 2 * CONFIG.global.cluster.margin) / 2,
              y: clusterHeight - CONFIG.global.text.fontSize / 2,
            },
            groupConfig: {
              draggable: true,
              x: index * clusterWidth + CONFIG.global.cluster.margin,
              y: CONFIG.global.cluster.margin,
              id: cluster.label
            }
          };
      });
      }
    },
    onClusterDragMove(event) {
      this.removeAlignmentLines();
      this.removeEqualDistanceIndicators();

      const clusterNode = event.target;

      const alignmentLines = CanvasClusterPositioningHelper.getAlignmentLines(clusterNode, this.stage);

      if (alignmentLines.length > 0) {
        this.drawAlignmentLines(alignmentLines);
        this.snapByOffset(clusterNode, alignmentLines);
      }

      const equalDistanceAlignment = CanvasClusterPositioningHelper.getEqualDistanceAlignment(clusterNode, this.stage);

      if (equalDistanceAlignment.equalDistanceIndicators.length > 0)  {
        this.drawEqualDistanceAlignment(equalDistanceAlignment);
        this.snapByLinesCoordinates(clusterNode, equalDistanceAlignment.alignmentLines);
      }
    },
    onClusterDragEnd() {
      this.removeAlignmentLines();
      this.removeEqualDistanceIndicators();
    },
    onProductDragMove(event) {
      this.removeAlignmentLines();
      this.removeEqualDistanceIndicators();

      const productNode = event.target;

      const alignmentLines = CanvasProductPositioningHelper.getAlignmentLines(productNode, this.stage);

      if (alignmentLines.length > 0) {
        this.drawAlignmentLines(alignmentLines);
        this.snapByOffset(productNode, alignmentLines);
      }

      const equalDistanceAlignment = CanvasProductPositioningHelper.getEqualDistanceAlignment(productNode, this.clustersProducts);

      if (equalDistanceAlignment.equalDistanceIndicators.length > 0)  {
        this.drawEqualDistanceAlignment(equalDistanceAlignment);
        this.snapByLinesCoordinates(productNode, equalDistanceAlignment.alignmentLines);
      }
    },
    onProductDragEnd() {
      this.removeAlignmentLines();
      this.removeEqualDistanceIndicators();
      this.updateClustersProducts();
    },
    removeAlignmentLines() {
      const lines = this.layer.find(`.${CONFIG.canvasPositioningHelper.alignmentLines.nodeName}`);
      lines.forEach(line => line.destroy());
    },
    removeEqualDistanceIndicators() {
      const lines = this.layer.find(`.${CONFIG.canvasPositioningHelper.equalDistanceIndicators.nodeName}`);
      lines.forEach(line => line.destroy());
    },
    drawEqualDistanceAlignment(equalDistanceAlignment) {
      equalDistanceAlignment.equalDistanceIndicators.forEach(indicator => {
        const indicatorLine = new Konva.Line({
          points: indicator.orientation === LineOrientation.Horizontal
          ? [
              indicator.fromCoordX, indicator.fromCoordY - 3,
              indicator.fromCoordX, indicator.fromCoordY + 3,
              indicator.fromCoordX, indicator.fromCoordY,
              indicator.toCoordX, indicator.toCoordY,
              indicator.toCoordX, indicator.toCoordY - 3,
              indicator.toCoordX, indicator.toCoordY + 3
            ]
          : [
              indicator.fromCoordX - 3, indicator.fromCoordY,
              indicator.fromCoordX + 3, indicator.fromCoordY,
              indicator.fromCoordX, indicator.fromCoordY,
              indicator.toCoordX, indicator.toCoordY,
              indicator.toCoordX - 3, indicator.toCoordY,
              indicator.toCoordX + 3, indicator.toCoordY
          ],
          stroke: CONFIG.canvasPositioningHelper.equalDistanceIndicators.stroke,
          strokeWidth:  CONFIG.canvasPositioningHelper.equalDistanceIndicators.strokeWidth,
          name: CONFIG.canvasPositioningHelper.equalDistanceIndicators.nodeName
        });

        this.layer.add(indicatorLine);
      });

      equalDistanceAlignment.alignmentLines.forEach(alignmentLine => {
        const coordinates = alignmentLine.coordinates;

        const line = new Konva.Line({
          points: [coordinates.fromCoordX, coordinates.fromCoordY, coordinates.toCoordX, coordinates.toCoordY],
          stroke: CONFIG.canvasPositioningHelper.alignmentLines.stroke,
          strokeWidth: CONFIG.canvasPositioningHelper.alignmentLines.strokeWidth,
          name: CONFIG.canvasPositioningHelper.alignmentLines.nodeName,
          dash: CONFIG.canvasPositioningHelper.alignmentLines.dash
        });

        this.layer.add(line);
      });
    },
    drawAlignmentLines(alignmentLines) {
      alignmentLines.forEach(this.drawAlignmentLine);
    },
    drawAlignmentLine(alignmentLine) {
      const line = new Konva.Line({
          points: alignmentLine.orientation === LineOrientation.Horizontal
            ? [-6000, 0, 6000, 0]
            : [0, -6000, 0, 6000],
          stroke: CONFIG.canvasPositioningHelper.alignmentLines.stroke,
          strokeWidth:  CONFIG.canvasPositioningHelper.alignmentLines.strokeWidth,
          name: CONFIG.canvasPositioningHelper.alignmentLines.nodeName,
          dash: CONFIG.canvasPositioningHelper.alignmentLines.dash
        });

      this.layer.add(line);

      line.absolutePosition({
        x: alignmentLine.orientation === LineOrientation.Vertical
          ? alignmentLine.line
          : 0,
        y: alignmentLine.orientation === LineOrientation.Horizontal
          ? alignmentLine.line
          : 0
      });
    },
    snapByOffset(node, alignmentLines) {
      const absolutePosition = node.absolutePosition();

      alignmentLines.forEach(line =>
        line.orientation === LineOrientation.Vertical
          ? absolutePosition.x = line.line + line.offset
          : absolutePosition.y = line.line + line.offset
      );

      node.absolutePosition(absolutePosition);
    },
    snapByLinesCoordinates(node, alignmentLines) {
      alignmentLines.forEach(alignmentLine => {
        switch (alignmentLine.snap) {
          case SnapType.Start:
            alignmentLine.orientation === LineOrientation.Vertical
              ? node.setX(alignmentLine.line)
              : node.setY(alignmentLine.line)
            break;

          case SnapType.Center:
            alignmentLine.orientation === LineOrientation.Vertical
              ? node.setX(alignmentLine.line - (node.getClientRect().width / 2))
              : node.setY(alignmentLine.line - (node.getClientRect().height / 2))
            break;

          case SnapType.End:
            alignmentLine.orientation === LineOrientation.Vertical
              ? node.setX(alignmentLine.line - (node.getClientRect().width))
              : node.setY(alignmentLine.line - (node.getClientRect().height))
            break;
        }
      });
    },
    updateClustersProducts() {
      const products = this.stage.find(`.${CONFIG.global.product.nodeName}`);
      const clusters = this.stage.find(`.${CONFIG.global.cluster.nodeName}`);

      const clustersProducts = {};

      for (let clusterId = 0; clusterId < clusters.length; clusterId++) {
        const cluster = clusters[clusterId];

        clustersProducts[clusterId] = {
          cluster: cluster,
          products: []
        };

        const clusterRectangle = cluster.find(`.${CONFIG.rectangle.nodeName}`)[0].getClientRect();

        for (const product of products) {
          const productRectangle = product.getClientRect();

          const isProductInsideCluster = productRectangle.x >= clusterRectangle.x
            && productRectangle.x + productRectangle.width <= clusterRectangle.x + clusterRectangle.width
            && productRectangle.y >= clusterRectangle.y
            && productRectangle.y + productRectangle.height <= clusterRectangle.y + clusterRectangle.height;

          if (isProductInsideCluster) {
            clustersProducts[clusterId].products.push(product);
          }
        }
      }

      this.clustersProducts = clustersProducts;
    },
    getState() {
      this.selectedShape = undefined;
      this.updateTransformer();

      const stage = this.$refs.stage.getStage();
      const layer = stage.children[0];
      const clusters = [];
      const product = [];
      let index = 0;

      let prewWidth = 20;

      this.sortClusters(layer);

      layer.children.forEach(node => {
        if (node.name() === CONFIG.nodeName.cluster) {
          const cluster = this.clustersStage[index];
          const scaleX  = cluster.children[0].attrs?.scaleX ? cluster.children[0].attrs.scaleX : 1;
          clusters.push({
            clusterName: cluster.attrs.id,
            pointX: (cluster.x() + cluster.children[0].x()) / this.currentWidth,
            pointY: cluster.y() / this.currentHeight,
            width: cluster.children[0].attrs.width * scaleX / this.currentWidth,
            height: cluster.children[0].attrs.height / this.currentHeight
          });
          prewWidth += cluster.children[0].attrs.width * scaleX / this.currentWidth + 20;
          index++;
        } else if (node.name() === CONFIG.nodeName.product) {
          product.push({
            productId: node.attrs.id,
            orthoPath: node.children[0].attrs.orthoPath,
            pointX: node.children[0].getClientRect().x / this.currentWidth,
            pointY: node.children[0].getClientRect().y / this.currentHeight,
            scaleX: node.children[0].scaleX() / this.currentWidth,
            scaleY: node.children[0].scaleY() / this.currentHeight,
          })
        }
      })

      this.createSVG();

      if (this.canvasData?.id) {
        return {
          id: this.canvasData.id,
          familyProducts: product,
          familyClusters: clusters,
          fileName: this.getFileName(this.selectedFamily),
        };
      } else {
        return {
          familyProducts: product,
          familyClusters: clusters,
          fileName: this.getFileName(this.selectedFamily),
        };
      }
    },

    clear() {
      this.clustersPreviews = [];
      this.productsPreviews = [];
    },

    createSVG() {
      const stage = this.$refs.stage.getStage();
      const layer = stage.children[0];

      layer.children.forEach(child => {
        if (child.attrs.name === 'product') {
          if (child.children[1].textArr.length === 1) {
            child.children[1].textArr.push({
              lastInParagraph: false,
              text: "",
              width: 10
            });
          }
        }
      });

      exportStageSVG(stage, false).then(data => {
        this.$emit('savePreviewData', FamilyPreviewService.createSvgUrl(data));
      });
    },

    getFileName(familyName) {
      switch (familyName) {
        case CONFIG.families.antennaSystem:
          return CONFIG.fileName.antennaSystem;
        case CONFIG.families.indoor:
          return CONFIG.fileName.indoor;
        case CONFIG.families.massiveMIMO:
          return CONFIG.fileName.massiveMIMO;
        case CONFIG.families.mobileTransport:
          return CONFIG.fileName.mobileTransport;
        case CONFIG.families.ranCompute:
          return CONFIG.fileName.ranCompute;
        case CONFIG.families.ranConnect:
          return CONFIG.fileName.ranConnect;
        case CONFIG.families.remoteRadio:
          return CONFIG.fileName.remoteRadio;
        case CONFIG.families.siteSystem:
          return CONFIG.fileName.siteSystem;
        case CONFIG.families.streetOutdoor:
          return CONFIG.fileName.streetOutdoor;
        default:
          break;
      }
    },

    sortClusters(layer) {
      this.clustersStage = layer.children.filter(node => node.attrs.name === 'cluster');

      this.clustersStage.sort((a, b) => {
        if (a.attrs.x > b.attrs.x) {
          return 1;
        }
        if (a.attrs.x < b.attrs.x) {
          return -1;
        }
        return 0;
      });
    },

    getProductName(productId) {
      const name = ProductService.getProducts().find(item => item.productId === productId)?.name;

      return NAMES.hasOwnProperty(name) ? NAMES[name] : name;
    }
  },
  
}
</script>

<style lang="less" scoped>
</style>
